import { useMutation, useQuery } from "@apollo/client";
import AddTask from "@mui/icons-material/AddTask";
import Archive from "@mui/icons-material/Archive";
import ArchiveOutlined from "@mui/icons-material/ArchiveOutlined";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Article from "@mui/icons-material/Article";
import Assignment from "@mui/icons-material/Assignment";
import Business from "@mui/icons-material/Business";
import Check from "@mui/icons-material/Check";
import MarkEmailRead from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnread from "@mui/icons-material/MarkEmailUnread";
import NotificationsActive from "@mui/icons-material/NotificationsActive";
import OpenInNew from "@mui/icons-material/OpenInNew";
import WorkHistory from "@mui/icons-material/WorkHistory";
import {
  Box,
  Button,
  Chip,
  ChipProps,
  CircularProgress,
  Divider,
  Link as MUILink,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import {
  GridActionsCellItem,
  type GridRowParams,
  type GridSortModel,
} from "@mui/x-data-grid-premium";
import { CreateLinkedTaskDialog } from "components/Notifications/CreateLinkedTaskDialog";
import { NotificationMarkdown } from "components/Notifications/NotificationMarkdown";
import {
  getNotificationCategoryLabel,
  getNotificationProgramAreaLabel,
} from "components/Notifications/notificationPresentation";
import { NotificationsTiles } from "components/Notifications/NotificationsTiles";
import { NotificationTypeIcon } from "components/Notifications/NotificationTypeIcon";
import { getVirtualEventId } from "encamp-shared/src/tasks/expandSeries";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  JobStatus,
  MyNotificationsQuery,
  NotificationUserQuery,
  NotificationView,
  ProgramArea,
  TaskStatus,
} from "generated-graphql/graphql";
import { GridSelectionProvider, OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import {
  ARCHIVE_NOTIFICATION,
  GET_NOTIFICATION_USER,
  GET_MY_NOTIFICATIONS,
  GET_MY_NOTIFICATION_VIEWS,
  GET_MY_UNREAD_NOTIFICATION_COUNT,
  MARK_NOTIFICATION_READ,
  MARK_NOTIFICATION_UNREAD,
} from "hooks/useNotifications";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { NotificationBulkActions } from "./NotificationBulkActions";

type NotificationRow = MyNotificationsQuery["myNotifications"]["items"][number];
type DirectNotificationRow = NonNullable<NotificationUserQuery["notificationUser"]>;
type NotificationTask = NonNullable<DirectNotificationRow["notification"]["task"]>;

function getTaskStatusColor(status: TaskStatus): ChipProps["color"] {
  if (status === TaskStatus.Completed) {
    return "success";
  }
  if (status === TaskStatus.Canceled) {
    return "default";
  }
  return "warning";
}

function getJobStatusColor(status: JobStatus): ChipProps["color"] {
  if (status === JobStatus.Succeeded || status === JobStatus.Promoted) {
    return "success";
  }
  if (
    status === JobStatus.Failed ||
    status === JobStatus.FailedMayAutoRetry ||
    status === JobStatus.FailedNeedsHuman ||
    status === JobStatus.FailedProvisioningEcsTask ||
    status === JobStatus.FailedQualityControlCheck
  ) {
    return "error";
  }
  if (
    status === JobStatus.Queued ||
    status === JobStatus.Dispatched ||
    status === JobStatus.Running ||
    status === JobStatus.ProvisioningEcsTask
  ) {
    return "info";
  }
  return "default";
}

function toUtcDateTime(value: unknown) {
  if (!value) {
    return DateTime.invalid("Missing date");
  }
  if (value instanceof Date) {
    return DateTime.fromJSDate(value, { zone: "utc" });
  }
  if (typeof value === "string") {
    return DateTime.fromISO(value, { zone: "utc" });
  }
  return DateTime.invalid("Unsupported date");
}

function formatDate(value: unknown) {
  const dateTime = toUtcDateTime(value);
  if (!dateTime.isValid) {
    return "Not recorded";
  }
  return dateTime.toLocaleString(DateTime.DATETIME_MED);
}

const NOTIFICATION_INITIAL_SORT_MODEL = [{ field: "createdAt", sort: "desc" as const }];

const NOTIFICATION_COMMAND_BUTTONS = [<NotificationBulkActions key="notification-bulk-actions" />];

const NOTIFICATION_GRID_SX = {
  "& .notification-row--unread": {
    bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.035),
  },
  "& .notification-row--unread:hover": {
    bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.07),
  },
  "& .notification-row--unread .MuiDataGrid-cell": {
    color: "text.primary",
    fontWeight: 700,
  },
  "& .notification-row--unread .MuiTypography-root": {
    color: "text.primary",
    fontWeight: 700,
  },
};

function getNotificationItems(data: MyNotificationsQuery) {
  return data.myNotifications.items;
}

function getNotificationCount(data: MyNotificationsQuery) {
  return data.myNotifications.count;
}

function removeLegacyNotificationSearchParam(search: string) {
  const searchParams = new URLSearchParams(search);
  searchParams.delete("notificationUserId");
  const nextSearch = searchParams.toString();
  return nextSearch ? `?${nextSearch}` : "";
}

function getTaskVirtualEventId(task: NotificationTask) {
  const startDate = toUtcDateTime(task.startDate);
  if (!startDate.isValid) {
    return null;
  }

  return getVirtualEventId(task.id, startDate.toJSDate());
}

function buildTaskUrl(tenantId: string, task: NotificationTask) {
  const virtualEventId = getTaskVirtualEventId(task);
  if (!virtualEventId) {
    return `/o/${tenantId}/tasks`;
  }

  return `/o/${tenantId}/tasks/${encodeURIComponent(virtualEventId)}`;
}

function LinkedObjectRow({
  icon,
  label,
  title,
  subtitle,
  href,
  hrefLabel = "Open",
  children,
}: {
  icon: ReactNode;
  label: string;
  title: string;
  subtitle?: string;
  href?: string;
  hrefLabel?: string;
  children?: ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            alignItems: "center",
            bgcolor: "action.hover",
            borderRadius: 1,
            color: "text.secondary",
            display: "flex",
            flexShrink: 0,
            height: 36,
            justifyContent: "center",
            width: 36,
          }}
        >
          {icon}
        </Box>
        <Stack spacing={0.75} sx={{ minWidth: 0, width: "100%" }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>
                {title}
              </Typography>
            </Box>
            {href ? (
              <MUILink
                component={RouterLink}
                to={href}
                underline="hover"
                sx={{
                  alignItems: "center",
                  display: "inline-flex",
                  fontSize: 13,
                  gap: 0.5,
                  whiteSpace: "nowrap",
                }}
              >
                {hrefLabel}
                <OpenInNew fontSize="inherit" />
              </MUILink>
            ) : null}
          </Stack>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
              {subtitle}
            </Typography>
          ) : null}
          {children}
        </Stack>
      </Stack>
    </Paper>
  );
}

function NotificationLinkedObjects({
  notification,
  tenantId,
}: {
  notification: DirectNotificationRow["notification"];
  tenantId: string;
}) {
  const facility = notification.facility;
  const task = notification.task;
  const job = notification.job;
  const tierIIReport = notification.tierIIReport;
  const submission = notification.submission;
  const hasLinkedObjects = Boolean(facility || task || job || tierIIReport || submission);

  if (!hasLinkedObjects) {
    return null;
  }

  return (
    <Stack spacing={1.5}>
      {facility ? (
        <LinkedObjectRow
          icon={<Business fontSize="small" />}
          label="Facility"
          title={facility.name ?? "Facility"}
          href={`/o/${tenantId}/facilities/${facility.id}/profile`}
        />
      ) : null}
      {task ? (
        <LinkedObjectRow
          icon={<Assignment fontSize="small" />}
          label="Task"
          title={task.title ?? "Task"}
          subtitle={`Due ${formatDate(task.startDate)} to ${formatDate(task.endDate)}`}
          href={buildTaskUrl(tenantId, task)}
          hrefLabel="Open in Tasks"
        >
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={prettyPrintEnumValue(task.status)}
              color={getTaskStatusColor(task.status)}
              variant="outlined"
            />
            {task.facilities?.map((taskFacility) => (
              <Chip key={taskFacility.id} size="small" label={taskFacility.name ?? "Facility"} />
            ))}
          </Stack>
        </LinkedObjectRow>
      ) : null}
      {job ? (
        <LinkedObjectRow
          icon={<WorkHistory fontSize="small" />}
          label="Job"
          title={job.task ? prettyPrintEnumValue(job.task) : "Background job"}
          subtitle={`Created ${formatDate(job.createdAt)}`}
        >
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={prettyPrintEnumValue(job.status)}
              color={getJobStatusColor(job.status)}
              variant="outlined"
            />
            {job.facility ? <Chip size="small" label={job.facility.name ?? "Facility"} /> : null}
          </Stack>
        </LinkedObjectRow>
      ) : null}
      {tierIIReport ? (
        <LinkedObjectRow
          icon={<Article fontSize="small" />}
          label="Tier II report"
          title={`${tierIIReport.reportingYear} ${prettyPrintEnumValue(tierIIReport.reportKind)}`}
          href={`/o/${tenantId}/chemicals/reports/${tierIIReport.id}`}
        />
      ) : null}
      {submission ? (
        <LinkedObjectRow
          icon={<Article fontSize="small" />}
          label="Submission"
          title={submission.title}
          subtitle={prettyPrintEnumValue(submission.submissionType)}
        >
          <Chip size="small" label={prettyPrintEnumValue(submission.status)} variant="outlined" />
        </LinkedObjectRow>
      ) : null}
    </Stack>
  );
}

export function NotificationDetail({ notificationUserId }: { notificationUserId: string }) {
  const { tenantId } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const listSearch = removeLegacyNotificationSearchParam(location.search);
  const listUrl = `/notifications${listSearch}`;
  const autoReadIdsRef = useRef<Set<string>>(new Set());
  const userToggledReadRef = useRef<Set<string>>(new Set());
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const refetchQueries = [GET_MY_NOTIFICATIONS, GET_MY_UNREAD_NOTIFICATION_COUNT];
  const {
    data: notificationData,
    loading: notificationLoading,
    refetch: refetchNotification,
  } = useQuery(GET_NOTIFICATION_USER, {
    variables: { id: notificationUserId },
    fetchPolicy: "cache-and-network",
  });
  const [markRead, markReadState] = useMutation(MARK_NOTIFICATION_READ, {
    refetchQueries,
  });
  const [markUnread, markUnreadState] = useMutation(MARK_NOTIFICATION_UNREAD, {
    refetchQueries,
  });
  const [archiveNotification, archiveState] = useMutation(ARCHIVE_NOTIFICATION, { refetchQueries });
  const notificationUser = notificationData?.notificationUser;
  const notification = notificationUser?.notification;
  const isMutating = markReadState.loading || markUnreadState.loading || archiveState.loading;

  useBreadcrumb([
    { label: "Notifications", to: listUrl },
    { label: notification?.title ?? "Notification" },
  ]);

  useEffect(() => {
    if (
      !notificationUser ||
      notificationUser.readAt ||
      autoReadIdsRef.current.has(notificationUser.id) ||
      userToggledReadRef.current.has(notificationUser.id)
    ) {
      return;
    }

    autoReadIdsRef.current.add(notificationUser.id);
    void markRead({
      variables: { notificationUserId: notificationUser.id },
      optimisticResponse: {
        markNotificationRead: {
          __typename: "NotificationUser" as const,
          id: notificationUser.id,
          readAt: new Date().toISOString(),
        },
      },
    }).catch(() => {});
  }, [markRead, notificationUser]);

  const navigateToList = () => {
    navigate(listUrl);
  };

  const handleToggleRead = async () => {
    if (!notificationUser) {
      return;
    }

    userToggledReadRef.current.add(notificationUser.id);
    const nowIso = new Date().toISOString();
    if (notificationUser.readAt) {
      await markUnread({
        variables: { notificationUserId: notificationUser.id },
        optimisticResponse: {
          markNotificationUnread: {
            __typename: "NotificationUser" as const,
            id: notificationUser.id,
            readAt: null,
          },
        },
      });
    } else {
      await markRead({
        variables: { notificationUserId: notificationUser.id },
        optimisticResponse: {
          markNotificationRead: {
            __typename: "NotificationUser" as const,
            id: notificationUser.id,
            readAt: nowIso,
          },
        },
      });
    }
  };

  const handleArchive = async () => {
    if (!notificationUser) {
      return;
    }

    const nowIso = new Date().toISOString();
    await archiveNotification({
      variables: { notificationUserId: notificationUser.id },
      optimisticResponse: {
        archiveNotification: {
          __typename: "NotificationUser" as const,
          id: notificationUser.id,
          readAt: notificationUser.readAt ?? nowIso,
          archivedAt: nowIso,
        },
      },
    });
    navigateToList();
  };

  if (notificationLoading && !notificationUser) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!notificationUser || !notification) {
    return (
      <Stack spacing={2} sx={{ py: 3, px: { xs: 2, md: 3 } }}>
        <Button startIcon={<ArrowBack />} onClick={navigateToList} sx={{ alignSelf: "flex-start" }}>
          Notifications
        </Button>
        <Paper variant="outlined" sx={{ borderRadius: 1, p: 3 }}>
          <Typography variant="h6">Notification not found</Typography>
          <Typography variant="body2" color="text.secondary">
            The notification may have been archived or is no longer available to your user.
          </Typography>
        </Paper>
      </Stack>
    );
  }

  let readToggleLabel = "Mark as read";
  if (notificationUser.archivedAt) {
    readToggleLabel = notificationUser.readAt ? "Read" : "Unread";
  } else if (notificationUser.readAt) {
    readToggleLabel = "Mark as unread";
  }

  const linkedTenantId = notification.tenant?.id ?? tenantId;
  const taskUrl =
    linkedTenantId && notification.task
      ? buildTaskUrl(linkedTenantId, notification.task)
      : undefined;
  const isResolved = Boolean(notification.resolvedAt);
  const isArchived = Boolean(notificationUser.archivedAt);
  const isUnread = !notificationUser.readAt;
  const canCreateTask = !notification.task && !isArchived && Boolean(linkedTenantId);

  const handleLinkedTaskCreated = async () => {
    setCreateTaskOpen(false);
    await refetchNotification();
  };

  return (
    <Stack py={3} spacing={2} sx={{ width: "100%", px: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Button startIcon={<ArrowBack />} onClick={navigateToList} sx={{ alignSelf: "flex-start" }}>
          Notifications
        </Button>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {taskUrl ? (
            <Button
              component={RouterLink}
              to={taskUrl}
              variant="outlined"
              startIcon={<Assignment />}
              endIcon={<OpenInNew />}
            >
              Open task
            </Button>
          ) : canCreateTask ? (
            <Button
              variant="outlined"
              startIcon={<AddTask />}
              onClick={() => setCreateTaskOpen(true)}
            >
              Create task
            </Button>
          ) : null}
          <Button
            variant="outlined"
            startIcon={notificationUser.readAt ? <MarkEmailUnread /> : <MarkEmailRead />}
            onClick={handleToggleRead}
            disabled={isMutating || isArchived}
          >
            {readToggleLabel}
          </Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<Archive />}
            onClick={handleArchive}
            disabled={isMutating || isArchived}
          >
            {isArchived ? "Archived" : "Archive"}
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 1, overflow: "hidden" }}>
        <Stack spacing={0}>
          <Box
            sx={{
              bgcolor: "background.default",
              px: { xs: 2, md: 3 },
              py: 2.5,
            }}
          >
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    flexShrink: 0,
                    "& .MuiSvgIcon-root, & svg": { fontSize: 36 },
                  }}
                >
                  <NotificationTypeIcon
                    category={notification.category}
                    programArea={notification.programArea ?? null}
                  />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    overflowWrap: "anywhere",
                    lineHeight: 1.2,
                    minWidth: 0,
                  }}
                >
                  {notification.title}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ pl: { xs: 0, sm: 6.5 } }}
              >
                <Chip
                  size="small"
                  label={getNotificationCategoryLabel(notification.category)}
                  variant="outlined"
                />
                {notification.programArea ? (
                  <Chip
                    size="small"
                    label={getNotificationProgramAreaLabel(notification.programArea)}
                    variant="outlined"
                  />
                ) : null}
                <Chip
                  size="small"
                  label={isUnread ? "Unread" : "Read"}
                  color={isUnread ? "primary" : "default"}
                  variant={isUnread ? "filled" : "outlined"}
                />
                {isResolved ? (
                  <Chip size="small" label="Resolved" color="success" variant="outlined" />
                ) : null}
                {isArchived ? <Chip size="small" label="Archived" variant="outlined" /> : null}
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {formatDate(notification.createdAt)}
                </Typography>
              </Stack>
            </Stack>
          </Box>
          <Divider />
          <Box
            sx={{
              maxWidth: 820,
              px: { xs: 2, md: 3 },
              py: 3,
              "& .MuiTypography-root": { fontSize: "1rem" },
              "& .MuiTypography-root:last-child": { mb: 0 },
            }}
          >
            <NotificationMarkdown bodyMarkdown={notification.bodyMarkdown} />
          </Box>
        </Stack>
      </Paper>

      {linkedTenantId ? (
        <NotificationLinkedObjects notification={notification} tenantId={linkedTenantId} />
      ) : null}

      {createTaskOpen && linkedTenantId ? (
        <CreateLinkedTaskDialog
          open={createTaskOpen}
          onClose={() => setCreateTaskOpen(false)}
          onCreated={handleLinkedTaskCreated}
          notificationId={notification.id}
          tenantId={linkedTenantId}
          defaultTitle={notification.title}
          bodyMarkdown={notification.bodyMarkdown ?? ""}
          defaultFacilityId={notification.facility?.id ?? null}
        />
      ) : null}
    </Stack>
  );
}

function isNotificationView(value: string): value is NotificationView {
  return Object.values(NotificationView).includes(value as NotificationView);
}

function NotificationsTable() {
  return (
    <GridSelectionProvider>
      <NotificationsTableContent />
    </GridSelectionProvider>
  );
}

function NotificationsTableContent() {
  useBreadcrumb({ label: "Notifications" });
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view");
  const activeView: NotificationView | null =
    viewParam && isNotificationView(viewParam) ? viewParam : null;

  const refetchQueries = [
    GET_MY_NOTIFICATIONS,
    GET_MY_UNREAD_NOTIFICATION_COUNT,
    GET_MY_NOTIFICATION_VIEWS,
  ];
  const [markRead] = useMutation(MARK_NOTIFICATION_READ, { refetchQueries });
  const [markUnread] = useMutation(MARK_NOTIFICATION_UNREAD, {
    refetchQueries,
  });
  const [archiveNotification] = useMutation(ARCHIVE_NOTIFICATION, {
    refetchQueries,
  });

  const handleRowClick = useCallback(
    (params: GridRowParams<NotificationRow>) => {
      navigate(`${encodeURIComponent(params.row.id)}${location.search}`);
    },
    [location.search, navigate]
  );

  const handleToggleRead = useCallback(
    (row: NotificationRow) => {
      const notificationUserId = row.id;
      if (row.readAt) {
        void markUnread({
          variables: { notificationUserId },
          optimisticResponse: {
            markNotificationUnread: {
              __typename: "NotificationUser" as const,
              id: notificationUserId,
              readAt: null,
            },
          },
        }).catch(() => {});
      } else {
        void markRead({
          variables: { notificationUserId },
          optimisticResponse: {
            markNotificationRead: {
              __typename: "NotificationUser" as const,
              id: notificationUserId,
              readAt: new Date().toISOString(),
            },
          },
        }).catch(() => {});
      }
    },
    [markRead, markUnread]
  );

  const handleArchive = useCallback(
    (row: NotificationRow) => {
      const notificationUserId = row.id;
      const nowIso = new Date().toISOString();
      void archiveNotification({
        variables: { notificationUserId },
        optimisticResponse: {
          archiveNotification: {
            __typename: "NotificationUser" as const,
            id: notificationUserId,
            readAt: row.readAt ?? nowIso,
            archivedAt: nowIso,
          },
        },
      }).catch(() => {});
    },
    [archiveNotification]
  );

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      search: omnisearch || undefined,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((entry) => ({ field: entry.field, sort: entry.sort })),
      includeArchived: false,
      view: activeView ?? undefined,
    }),
    [activeView]
  );

  const getNotificationRowClassName = useCallback(
    (params: GridRowParams<NotificationRow>) =>
      !params.row.readAt ? "notification-row--unread" : "",
    []
  );

  const columns = useMemo(
    (): OmnisearchGridColDef<NotificationRow>[] => [
      {
        field: "title",
        headerName: "Title",
        flex: 1,
        valueGetter(_, row) {
          return row.notification.title;
        },
        renderCell(params) {
          return (
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
              <NotificationTypeIcon
                category={params.row.notification.category}
                programArea={params.row.notification.programArea ?? null}
              />
              <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                {params.row.notification.title}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "programArea",
        headerName: "Program",
        flex: 0.28,
        valueGetter(_, row) {
          return row.notification.programArea
            ? getNotificationProgramAreaLabel(row.notification.programArea)
            : "";
        },
        renderCell(params) {
          if (!params.row.notification.programArea) {
            return null;
          }

          return (
            <Typography variant="body2" color="text.secondary" noWrap>
              {getNotificationProgramAreaLabel(params.row.notification.programArea)}
            </Typography>
          );
        },
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(ProgramArea),
        enumPresentationFunction: (value) => getNotificationProgramAreaLabel(value),
      },
      {
        field: "facility",
        headerName: "Facility",
        flex: 0.5,
        valueGetter(_, row) {
          return row.notification.facility?.name ?? "";
        },
      },
      {
        field: "createdAt",
        headerName: "Received",
        flex: 0.45,
        filterKeyType: "date",
        valueGetter(_, row) {
          return formatDate(row.createdAt);
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        flex: 0.12,
        minWidth: 48,
        getActions(params: GridRowParams<NotificationRow>) {
          const isRead = Boolean(params.row.readAt);
          return [
            <GridActionsCellItem
              key="toggle-read"
              icon={isRead ? <NotificationsActive fontSize="small" /> : <Check fontSize="small" />}
              label={isRead ? "Mark as unread" : "Mark as read"}
              onClick={() => handleToggleRead(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              key="archive"
              icon={<ArchiveOutlined fontSize="small" />}
              label="Archive"
              onClick={() => handleArchive(params.row)}
              showInMenu
            />,
          ];
        },
      },
    ],
    [handleArchive, handleToggleRead]
  );

  const handleSelectView = (next: NotificationView | null) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next) {
      // Tile selection is mutually exclusive with column-driven filters; clear
      // any URL-persisted filter/search state when activating a tile.
      Array.from(nextParams.keys()).forEach((key) => {
        if (key !== "view") nextParams.delete(key);
      });
      nextParams.set("view", next);
    } else {
      nextParams.delete("view");
    }
    setSearchParams(nextParams, { replace: false });
  };

  return (
    <Stack py={3} spacing={2}>
      <Box sx={{ px: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Your inbox across all organizations. Read state is personal — marking a notification read
          here does not change it for other recipients.
        </Typography>
        <NotificationsTiles activeView={activeView} onSelect={handleSelectView} />
      </Box>
      <OmnisearchDataGridV2
        query={GET_MY_NOTIFICATIONS}
        persistenceKey="customerNotifications"
        checkboxSelection
        disableRowSelectionOnClick
        columns={columns}
        getItems={getNotificationItems}
        getCount={getNotificationCount}
        buildVariables={buildVariables}
        commandButtons={NOTIFICATION_COMMAND_BUTTONS}
        onRowClick={handleRowClick}
        getRowClassName={getNotificationRowClassName}
        gridSx={NOTIFICATION_GRID_SX}
        initialSortModel={NOTIFICATION_INITIAL_SORT_MODEL}
        noRowsMessage="No notifications"
        sx={{ pt: 0 }}
      />
    </Stack>
  );
}

export function Notifications() {
  const { notificationUserId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const legacyNotificationUserId = searchParams.get("notificationUserId");

  useEffect(() => {
    if (notificationUserId || !legacyNotificationUserId) {
      return;
    }

    const nextSearch = removeLegacyNotificationSearchParam(`?${searchParams.toString()}`);
    navigate(`${encodeURIComponent(legacyNotificationUserId)}${nextSearch}`, {
      replace: true,
    });
  }, [legacyNotificationUserId, navigate, notificationUserId, searchParams]);

  if (notificationUserId) {
    return <NotificationDetail notificationUserId={notificationUserId} />;
  }

  return <NotificationsTable />;
}
